import { existsSync } from "fs";
import { readFile } from "fs/promises";
import { basename, resolve } from "pathe";
import { pathToFileURL } from "url";
/**
 * Loads configuration from config file or package.json.
 */
export class ConfigLoader {
    options;
    configFiles;
    constructor(options) {
        this.options = options;
        this.configFiles = [
            `${options.toolName}.config.ts`,
            `${options.toolName}.config.mts`,
            `${options.toolName}.config.js`,
            `${options.toolName}.config.mjs`,
        ];
    }
    /**
     * Loads configuration from the specified directory.
     *
     * @param cwd - Directory to search for config files
     * @returns Configuration and config file path (if found)
     */
    async load(cwd = process.cwd()) {
        // Try config files first
        for (const configFile of this.configFiles) {
            const configPath = resolve(cwd, configFile);
            if (existsSync(configPath)) {
                const config = await this.loadConfigFile(configPath);
                return { config, configPath };
            }
        }
        // Try package.json
        const packageJsonPath = resolve(cwd, "package.json");
        if (existsSync(packageJsonPath)) {
            const config = await this.loadFromPackageJson(packageJsonPath);
            if (config) {
                return { config, configPath: packageJsonPath };
            }
        }
        return { config: {} };
    }
    /**
     * Loads configuration from an explicitly requested config file.
     *
     * @param configPath - Path to the config file (`.ts`, `.js`, or a package.json)
     * @throws Error if the file does not exist, or if it exists but fails to
     *   load/parse - unlike auto-discovery, silently continuing with no
     *   configuration here would produce wrong output for a path the caller
     *   named explicitly.
     */
    async loadFrom(configPath) {
        const resolvedPath = resolve(configPath);
        if (!existsSync(resolvedPath)) {
            throw new Error(`Config file not found: ${resolvedPath}`);
        }
        if (basename(resolvedPath) === "package.json") {
            const config = await this.loadFromPackageJson(resolvedPath, { throwOnError: true });
            return { config: config ?? {}, configPath: resolvedPath };
        }
        return {
            config: await this.loadConfigFile(resolvedPath, { throwOnError: true }),
            configPath: resolvedPath,
        };
    }
    /**
     * Loads configuration from a TypeScript/JavaScript config file.
     *
     * @param options.throwOnError - Rethrow load failures instead of warning and
     * returning an empty config. Used for an explicitly requested path (`loadFrom`),
     * where silently continuing with no configuration would produce wrong output;
     * not used for auto-discovered well-known filenames, where a config file is optional.
     */
    async loadConfigFile(configPath, options) {
        try {
            const fileUrl = pathToFileURL(configPath).href;
            const module = await import(fileUrl);
            return module.default || module;
        }
        catch (error) {
            if (options?.throwOnError) {
                throw new Error(`Failed to load config from ${configPath}: ${error.message}`);
            }
            console.warn(`Warning: Failed to load config from ${configPath}:`, error.message);
            return {};
        }
    }
    /**
     * Loads configuration from package.json's tool-name field.
     *
     * @param options.throwOnError - Rethrow read/parse failures instead of
     * warning and returning null. Used for an explicitly requested path
     * (`loadFrom`), matching `loadConfigFile`'s throwOnError; not used for
     * auto-discovered package.json, where a config file is optional.
     */
    async loadFromPackageJson(packageJsonPath, options) {
        let content;
        try {
            content = await readFile(packageJsonPath, "utf-8");
        }
        catch (error) {
            if (options?.throwOnError) {
                throw new Error(`Failed to load config from ${packageJsonPath}: ${error.message}`);
            }
            // File read error (permissions, not found, etc.) - silently return null
            // since package.json config is optional
            return null;
        }
        try {
            const packageJson = JSON.parse(content);
            const field = packageJson[this.options.toolName];
            if (field && typeof field === "object") {
                return field;
            }
            return null;
        }
        catch (error) {
            if (options?.throwOnError) {
                throw new Error(`Failed to parse ${packageJsonPath}: ${error.message}`);
            }
            // JSON parse error - warn the user since this is likely a syntax error
            console.warn(`Warning: Failed to parse ${packageJsonPath}: ${error.message}`);
            return null;
        }
    }
}
/**
 * Defines a configuration with type checking.
 *
 * @example
 * ```typescript
 * import { defineConfig } from 'zinfer'; // or 'vinfer'
 *
 * export default defineConfig({
 *   include: ['src/** /*.schema.ts'],
 *   outDir: 'src/types',
 *   suffix: 'Schema',
 * });
 * ```
 */
export function defineConfig(config) {
    return config;
}
//# sourceMappingURL=config-loader.js.map